import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { getIntercomJwt, INTERCOM_APP_ID } from "@/lib/intercom.functions";

declare global {
  interface Window {
    Intercom?: ((...args: unknown[]) => void) & { q?: unknown[]; c?: (args: unknown) => void };
    intercomSettings?: Record<string, unknown>;
    attachEvent?: (event: string, listener: () => void) => void;
    __lumvioIntercomTrack?: (name: string, metadata?: Record<string, unknown>) => void;
    __lumvioIntercomUpdate?: (attrs: Record<string, unknown>) => void;
  }
}

function loadIntercomScript(appId: string) {
  if (typeof window === "undefined") return;
  const w = window;
  const ic = w.Intercom;
  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", w.intercomSettings);
    return;
  }
  const i: ((...args: unknown[]) => void) & { q?: unknown[]; c?: (args: unknown) => void } =
    function (...args: unknown[]) {
      (i.q = i.q || []).push(args);
    } as never;
  i.q = [];
  i.c = function (args) {
    i.q!.push(args);
  };
  w.Intercom = i;
  const load = () => {
    if (document.getElementById("intercom-widget-script")) return;
    const s = document.createElement("script");
    s.id = "intercom-widget-script";
    s.type = "text/javascript";
    s.async = true;
    s.src = `https://widget.intercom.io/widget/${appId}`;
    s.onerror = () => {
      // Fail silently — app must not break if Intercom cannot load
    };
    const first = document.getElementsByTagName("script")[0];
    first?.parentNode?.insertBefore(s, first);
  };
  // Defer the widget until the browser is idle (or the visitor interacts), so
  // its script never competes with first paint / hydration. The launcher still
  // appears automatically without any user action.
  const schedule = () => {
    const idle = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
      }
    ).requestIdleCallback;
    if (idle) idle(load, { timeout: 6000 });
    else window.setTimeout(load, 3000);
  };
  const onFirstInteraction = () => {
    load();
    ["pointerdown", "keydown", "touchstart"].forEach((e) =>
      window.removeEventListener(e, onFirstInteraction),
    );
  };
  ["pointerdown", "keydown", "touchstart"].forEach((e) =>
    window.addEventListener(e, onFirstInteraction, { once: true, passive: true }),
  );
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}

export function IntercomProvider() {
  const { user, profile } = useAuth();
  const { i18n } = useTranslation();
  const refreshTimer = useRef<number | null>(null);
  const bootedFor = useRef<string | null>(null);

  // Load script once
  useEffect(() => {
    try {
      loadIntercomScript(INTERCOM_APP_ID);
    } catch {
      // ignore
    }
    return () => {
      try {
        window.Intercom?.("shutdown");
      } catch {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    const lang = i18n.language?.startsWith("ar") ? "ar" : "en";
    // Keep the launcher clear of mobile safe-area insets (home indicator) and page CTAs.
    const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
    let safeBottom = 0;
    if (typeof window !== "undefined") {
      const probe = document.createElement("div");
      probe.style.cssText =
        "position:fixed;left:-9999px;bottom:0;height:env(safe-area-inset-bottom,0px);pointer-events:none;";
      document.body.appendChild(probe);
      safeBottom = probe.getBoundingClientRect().height || 0;
      probe.remove();
    }
    const baseAttrs: Record<string, unknown> = {
      app_id: INTERCOM_APP_ID,
      language_override: lang,
      alignment: "right",
      horizontal_padding: isMobile ? 16 : 20,
      vertical_padding: Math.round((isMobile ? 20 : 20) + safeBottom),
      action_color: "#6366F1",
      background_color: "#7C3AED",
    };

    const clearRefresh = () => {
      if (refreshTimer.current) {
        window.clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };

    const bootAnon = () => {
      clearRefresh();
      bootedFor.current = null;
      try {
        window.Intercom?.("shutdown");
        window.Intercom?.("boot", baseAttrs);
      } catch {
        // ignore
      }
    };

    const bootAuthed = async () => {
      if (!user || !profile) return;
      try {
        const { data: sessionRes } = await supabase.auth.getSession();
        if (!sessionRes.session) {
          bootAnon();
          return;
        }
        const res = await getIntercomJwt();
        const attrs: Record<string, unknown> = {
          ...baseAttrs,
          intercom_user_jwt: res.token,
          user_id: res.userId,
          email: res.email,
          name: res.name,
          created_at: res.createdAt,
          role: profile.role,
          plan: profile.subscription_tier,
          identity_verified: profile.kyc_status === "verified",
          projects_count: profile.projects_created_this_month ?? 0,
          account_status: "active",
          is_banned: false,
        };
        if (bootedFor.current === user.id) {
          window.Intercom?.("update", attrs);
        } else {
          window.Intercom?.("shutdown");
          window.Intercom?.("boot", attrs);
          bootedFor.current = user.id;
        }
        // Refresh 5 min before expiry
        clearRefresh();
        refreshTimer.current = window.setTimeout(
          () => {
            void bootAuthed();
          },
          Math.max(60_000, (res.expiresIn - 300) * 1000),
        );
      } catch {
        // Fail silently but ensure anon session exists
        if (!bootedFor.current) bootAnon();
      }
    };

    if (user && profile) {
      void bootAuthed();
    } else {
      bootAnon();
    }

    return () => {
      clearRefresh();
    };
  }, [user, profile, i18n.language]);

  // Expose helpers for events / manual updates
  useEffect(() => {
    window.__lumvioIntercomTrack = (name, metadata) => {
      try {
        window.Intercom?.("trackEvent", name, metadata);
      } catch {
        // ignore
      }
    };
    window.__lumvioIntercomUpdate = (attrs) => {
      try {
        window.Intercom?.("update", attrs);
      } catch {
        // ignore
      }
    };
  }, []);

  return null;
}

export function trackIntercomEvent(name: string, metadata?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.__lumvioIntercomTrack?.(name, metadata);
  } catch {
    // ignore
  }
}

export function updateIntercomAttrs(attrs: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.__lumvioIntercomUpdate?.(attrs);
  } catch {
    // ignore
  }
}
